import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {
  getClientCoupons,
  createClientCoupon,
  updateClientCoupon,
  deleteClientCoupon,
  getClientCouponUsages,
  getClients,
} from "../../api/adminService";
import { getFeeSettings } from "../../api/feesSubscriptionsService";

const DISCOUNT_TYPE = { PERCENTAGE: 1, FIXED_AMOUNT: 2 };
const FUNDING_TYPE = { STORE_FUNDED: 1, PLATFORM_FUNDED: 2 };

function toDateInputValue(value) {
  if (!value) return "";
  return value.slice(0, 10);
}

function discountLabel(coupon) {
  return coupon.discountType === DISCOUNT_TYPE.FIXED_AMOUNT
    ? `${coupon.discountValue} ₪ off`
    : `${coupon.discountValue}% off`;
}

function statusChip(coupon) {
  if (coupon.isExpired) return <Chip label="Expired" size="small" color="default" />;
  if (!coupon.isActive) return <Chip label="Inactive" size="small" color="default" />;
  return <Chip label="Active" size="small" color="success" />;
}

function fundingChip(coupon) {
  if (coupon.fundingType !== FUNDING_TYPE.PLATFORM_FUNDED) return null;
  return <Chip label="We pay (platform-funded)" size="small" color="primary" variant="outlined" />;
}

function usageChip(coupon, onClick) {
  const used = coupon.usedCount || 0;
  if (used === 0) return <Chip label="Not used yet" size="small" variant="outlined" />;
  return (
    <Chip
      label={`Used ${used} time${used === 1 ? "" : "s"}`}
      size="small"
      color="info"
      onClick={onClick}
      clickable
    />
  );
}

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null); // { coupon? }
  const [usagesDialog, setUsagesDialog] = useState(null); // { coupon }
  const [storeCommissionPercent, setStoreCommissionPercent] = useState(null);

  async function reload() {
    const data = await getClientCoupons();
    setCoupons(data);
  }

  useEffect(() => {
    reload().catch(() => setError("Could not load coupons. Is the API running?"));
    getFeeSettings()
      .then((settings) => setStoreCommissionPercent(settings.storeCommissionPercent))
      .catch(() => {});
  }, []);

  function openCreateDialog() {
    setDialog({ coupon: null });
  }

  function openEditDialog(coupon) {
    setDialog({ coupon });
  }

  async function handleSave(payload) {
    const { coupon } = dialog;
    try {
      if (coupon) {
        await updateClientCoupon(coupon.clientCouponId, payload);
      } else {
        await createClientCoupon(payload);
      }
      setDialog(null);
      await reload();
    } catch (err) {
      setError(err.response?.data || "Could not save this coupon.");
    }
  }

  async function handleDelete(coupon) {
    if (!window.confirm(`Delete coupon "${coupon.code}"? This cannot be undone.`)) return;
    try {
      await deleteClientCoupon(coupon.clientCouponId);
      await reload();
    } catch (err) {
      setError(err.response?.data || "This coupon could not be deleted.");
    }
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Coupons</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          New Coupon
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : "Something went wrong."}
        </Alert>
      )}

      <List sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
        {coupons.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No coupons yet.
          </Typography>
        )}
        {coupons.map((coupon, index) => (
          <React.Fragment key={coupon.clientCouponId}>
            {index > 0 && <Divider component="li" />}
            <ListItem
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <IconButton size="small" onClick={() => openEditDialog(coupon)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(coupon)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">{coupon.code}</Typography>
                    <Chip label={discountLabel(coupon)} size="small" color="secondary" />
                    {statusChip(coupon)}
                    {fundingChip(coupon)}
                    {usageChip(coupon, () => setUsagesDialog({ coupon }))}
                  </Stack>
                }
                secondary={
                  (coupon.targetClients.length === 0
                    ? "For all clients"
                    : `For ${coupon.targetClients.map((c) => c.fullName).join(", ")}`) +
                  (coupon.minOrderAmount > 0 ? ` · Min. order ${coupon.minOrderAmount} ₪` : "") +
                  ` · ${toDateInputValue(coupon.startsAt)} – ${toDateInputValue(coupon.expiresAt)}`
                }
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>

      {dialog && (
        <CouponDialog
          dialog={dialog}
          onClose={() => setDialog(null)}
          onSave={handleSave}
          storeCommissionPercent={storeCommissionPercent}
        />
      )}

      {usagesDialog && (
        <UsagesDialog coupon={usagesDialog.coupon} onClose={() => setUsagesDialog(null)} />
      )}
    </Box>
  );
}

function UsagesDialog({ coupon, onClose }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getClientCouponUsages(coupon.clientCouponId, { pageSize: 50 })
      .then(setStats)
      .catch(() => setError("Could not load usage history."));
  }, [coupon.clientCouponId]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Usage history — {coupon.code}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!error && !stats && (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        )}
        {stats && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {stats.totalRedemptions} redemption{stats.totalRedemptions === 1 ? "" : "s"} · {stats.totalDiscountGiven} ₪ total
              discount given
            </Typography>
            <List sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
              {stats.usages.items.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  No redemptions yet.
                </Typography>
              )}
              {stats.usages.items.map((usage, index) => (
                <React.Fragment key={usage.clientCouponUsageId}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem>
                    <ListItemText
                      primary={`${usage.customerName}${usage.customerPhone ? ` (${usage.customerPhone})` : ""}`}
                      secondary={`Order #${usage.orderNumber} · ${usage.discountAmount} ₪ off · ${new Date(
                        usage.usedAt
                      ).toLocaleString()}`}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function CouponDialog({ dialog, onClose, onSave, storeCommissionPercent }) {
  const { coupon } = dialog;
  const [code, setCode] = useState(coupon?.code || "");
  const [discountType, setDiscountType] = useState(coupon?.discountType ?? DISCOUNT_TYPE.PERCENTAGE);
  const [discountValue, setDiscountValue] = useState(coupon?.discountValue ?? 10);
  const [minOrderAmount, setMinOrderAmount] = useState(coupon?.minOrderAmount ?? 0);
  const [fundingType, setFundingType] = useState(coupon?.fundingType ?? FUNDING_TYPE.STORE_FUNDED);
  const [target, setTarget] = useState(coupon?.targetClients?.length ? "specific" : "all");
  const [selectedClients, setSelectedClients] = useState(
    coupon?.targetClients?.map((c) => ({ userId: c.userId, fullName: c.fullName, phone: c.phone })) || []
  );
  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [startsAt, setStartsAt] = useState(toDateInputValue(coupon?.startsAt) || toDateInputValue(new Date().toISOString()));
  const [expiresAt, setExpiresAt] = useState(toDateInputValue(coupon?.expiresAt));
  const [isActive, setIsActive] = useState(coupon?.isActive ?? true);

  useEffect(() => {
    if (target !== "specific") return;
    const handle = setTimeout(() => {
      getClients({ search: clientSearch || undefined, pageSize: 10 })
        .then((data) => setClientOptions(data.items))
        .catch(() => setClientOptions([]));
    }, 300);
    return () => clearTimeout(handle);
  }, [clientSearch, target]);

  function handleTargetChange(e, value) {
    if (!value) return;
    setTarget(value);
    if (value === "all") setSelectedClients([]);
  }

  function handleDiscountTypeChange(e, value) {
    if (!value) return;
    setDiscountType(value);
    if (value === DISCOUNT_TYPE.PERCENTAGE && discountValue > 100) setDiscountValue(100);
  }

  function handleFundingTypeChange(e, value) {
    if (!value) return;
    setFundingType(value);
  }

  const isPlatformFunded = fundingType === FUNDING_TYPE.PLATFORM_FUNDED;
  // Mirrors the backend cap (ClientCouponsController.ValidateFundingAsync): a platform-funded
  // coupon is paid for out of the platform's own commission on the order, so its percentage
  // discount can't exceed the current store commission rate, or the platform would lose money.
  const platformFundedCapExceeded =
    isPlatformFunded &&
    discountType === DISCOUNT_TYPE.PERCENTAGE &&
    storeCommissionPercent != null &&
    discountValue > storeCommissionPercent;

  const validDiscountValue =
    (discountType === DISCOUNT_TYPE.PERCENTAGE
      ? discountValue > 0 && discountValue <= 100
      : discountValue > 0) && !platformFundedCapExceeded;

  const canSave =
    code.trim() &&
    validDiscountValue &&
    minOrderAmount >= 0 &&
    startsAt &&
    expiresAt &&
    (target === "all" || selectedClients.length > 0);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{coupon ? "Edit Coupon" : "New Coupon"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            fullWidth
            autoFocus
          />

          <ToggleButtonGroup
            value={discountType}
            exclusive
            onChange={handleDiscountTypeChange}
            size="small"
            fullWidth
          >
            <ToggleButton value={DISCOUNT_TYPE.PERCENTAGE}>Percentage</ToggleButton>
            <ToggleButton value={DISCOUNT_TYPE.FIXED_AMOUNT}>Fixed Amount (₪)</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label={discountType === DISCOUNT_TYPE.FIXED_AMOUNT ? "Discount Amount (₪)" : "Discount Percent (%)"}
            type="number"
            value={discountValue}
            onChange={(e) => setDiscountValue(Number(e.target.value))}
            slotProps={{
              htmlInput:
                discountType === DISCOUNT_TYPE.FIXED_AMOUNT ? { min: 0.01, step: 0.01 } : { min: 1, max: 100 },
            }}
            fullWidth
          />

          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Who pays for the discount
            </Typography>
            <ToggleButtonGroup
              value={fundingType}
              exclusive
              onChange={handleFundingTypeChange}
              size="small"
              fullWidth
            >
              <ToggleButton value={FUNDING_TYPE.STORE_FUNDED}>Store Funded</ToggleButton>
              <ToggleButton value={FUNDING_TYPE.PLATFORM_FUNDED}>Platform Funded (We Pay)</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {isPlatformFunded
                ? "The store gets paid as if there were no coupon at all — the discount comes out of our own commission on the order, not the store's profit."
                : "The store absorbs this discount out of its own payout, same as today."}
            </Typography>
            {platformFundedCapExceeded && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                A platform-funded coupon's discount can't exceed the current store commission (
                {storeCommissionPercent}%), since it's paid for out of that commission.
              </Alert>
            )}
          </Box>

          <TextField
            label="Minimum Order Amount (₪)"
            type="number"
            value={minOrderAmount}
            onChange={(e) => setMinOrderAmount(Number(e.target.value))}
            helperText="Leave at 0 for no minimum. Customers must spend at least this much for the coupon to apply."
            slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
            fullWidth
          />

          <ToggleButtonGroup value={target} exclusive onChange={handleTargetChange} size="small" fullWidth>
            <ToggleButton value="all">All Clients</ToggleButton>
            <ToggleButton value="specific">Specific Client</ToggleButton>
          </ToggleButtonGroup>

          {target === "specific" && (
            <Autocomplete
              multiple
              options={clientOptions}
              getOptionLabel={(option) => `${option.fullName} (${option.phone || "no phone"})`}
              isOptionEqualToValue={(option, value) => option.userId === value.userId}
              value={selectedClients}
              onChange={(e, value) => setSelectedClients(value)}
              onInputChange={(e, value) => setClientSearch(value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option.fullName} size="small" {...getTagProps({ index })} key={option.userId} />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Search clients by name or phone number" />}
            />
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              label="Start Date"
              type="date"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Expiry Date"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          {coupon && (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Active</Typography>
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={() =>
            onSave({
              code: code.trim(),
              discountType,
              discountValue,
              minOrderAmount,
              fundingType,
              targetUserIds: target === "specific" ? selectedClients.map((c) => c.userId) : [],
              startsAt,
              expiresAt,
              isActive,
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
